/**
 * VolumeSeries component renders volume bars as an overlay in the price area.
 * Subscribes to 'data' and 'scales' events for imperative updates.
 */

import React, { useMemo } from "react";

import styles from "../styles/styles.module.css";
import { useChartContext } from "../core/ChartContext";
import { CANDLESTICK_COLORS, generateVolumeBars } from "~utils/ticker-chart";
import { useChartSubscription } from "~hooks/ticker-chart";

export interface VolumeSeriesProps {
  /** Series ID to render */
  seriesId: string;
  /** Bar color (hex), default: uses up/down coloring */
  color?: string;
  /** Bar width in pixels (default: auto-calculated) */
  barWidth?: number;
  /** Spacing between bars in pixels (default: 1) */
  spacing?: number;
  /** Opacity (default: 0.5) */
  opacity?: number;
}

/**
 * VolumeSeries renders trading volume as bars overlaid behind the price series.
 * Updates imperatively without React re-renders.
 */
const VolumeSeriesInner: React.FC<VolumeSeriesProps> = ({
  seriesId,
  color,
  barWidth,
  spacing = 1,
  opacity = 0.5,
}) => {
  const store = useChartContext();

  // Subscribe to data and scales events - revision triggers useMemo recomputation
  const revision = useChartSubscription(store, ["data", "scales"]);

  const layout = store.getLayout();
  const priceArea = layout.priceArea;

  // Use getRenderableData for bar rendering (includes padding for smooth edges)
  const bars = useMemo(() => {
    const visibleData = store.getRenderableData(seriesId);
    const scales = store.getScales();

    if (!scales.volume || visibleData.length === 0) {
      return [];
    }

    return generateVolumeBars(visibleData, {
      xScale: scales.x,
      volumeScale: scales.volume,
      baselineY: priceArea.y + priceArea.height,
      barWidth,
      spacing,
      priceData: visibleData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, seriesId, store, priceArea, barWidth, spacing]);

  if (bars.length === 0) {
    return null;
  }

  return (
    <g
      className={styles.volumeSeries}
      clipPath={`url(#clip-volume-area-${seriesId})`}
      opacity={opacity}
      data-component="price-movement-chart-series-volume"
    >
      <defs>
        <clipPath id={`clip-volume-area-${seriesId}`}>
          <rect
            x={priceArea.x}
            y={priceArea.y}
            width={priceArea.width}
            height={priceArea.height}
          />
        </clipPath>
      </defs>

      {bars.map((bar) => {
        let barColor = color;

        if (!barColor) {
          if (bar.direction === "up") {
            barColor = CANDLESTICK_COLORS.up;
          } else if (bar.direction === "down") {
            barColor = CANDLESTICK_COLORS.down;
          } else {
            barColor = CANDLESTICK_COLORS.unchanged;
          }
        }

        return (
          <rect
            key={bar.timestamp}
            className={styles.volumeBar}
            x={bar.x}
            y={bar.y}
            width={bar.width}
            height={bar.height}
            fill={barColor}
          />
        );
      })}
    </g>
  );
};

export const VolumeSeries = React.memo(VolumeSeriesInner);
VolumeSeries.displayName = "VolumeSeries";
